import React, { useState, useMemo, useEffect } from "react";
import {
    Paper, Typography, Grid, TextField, Button, Box, Alert, Divider, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
    Table, TableHead, TableRow, TableCell, TableBody,
} from "@mui/material";
import PrintPanel from "./PrintPanel";
import {
    penaltyPdf, fmtPts, enrichLive, startlistPdf, runResultsPdf, eventResultsPdf, toEventLive, telemarkToLive, telemarkEventLive,
} from "./racePdf";
import { ensureOwnLogo } from "../../utils/pdfBranding";
import { penaltySupport, validateHeader } from "./raceTypes";
import { fileToDataUrl } from "./RaceSheetForm";
import { computePenaltyDetail, TM_FACTORS, racePointsFromTimes } from "./fisPenaltyCalc";

const MAIL_TYPES = [
    { value: "startlist", label: "Liste de départ (Startlist)" },
    { value: "partial", label: "Résultats partiels (Partial)" },
    { value: "unofficial", label: "Résultats non officiels (Unofficial)" },
    { value: "official", label: "Résultats officiels (Official)" },
];

// Export FIS : calcul officiel (FIS Points Rules art. 4.4.7, via
// fisPointsRouter.js) et envoi du XML par email ou API FIS Member.
export default function FisPanel({ config, setConfig, eventKey, apiFetch, saveConfig, setError, setStatus }) {
    const key = encodeURIComponent(eventKey);
    const support = penaltySupport(config);
    const { isValid, errors } = validateHeader(config);
    const set = (f, v) => setConfig((p) => ({ ...p, [f]: v }));

    const [run, setRun] = useState(1);
    // Par défaut : dernière manche de la course (résultats de l'épreuve).
    useEffect(() => {
        apiFetch(`/api/timing/run/${key}`)
            .then((r) => (r && r.ok ? r.json() : null))
            .then((info) => info?.runCount > 1 && setRun(info.runCount))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Télémark : classement avec pénalités (temps de ski + style + saut),
    // converti au format attendu par le XML FIS : temps de chaque manche
    // (pénalités comprises) et total.
    // Manche avant la dernière : classement de CETTE manche seule (view=run) ;
    // dernière manche : classement de l'épreuve (manches cumulées).
    const tmUrl = (runNumber, runCount) =>
        `/api/timing/results-telemark/${key}?run=${runNumber}${runNumber < (runCount || 1) ? "&view=run" : ""}`;
    const telemarkLive = async (runNumber, runCount) => {
        const r = await apiFetch(tmUrl(runNumber, runCount));
        if (!r || !r.ok) return null;
        const tm = await r.json();
        const multi = (tm.runCount || 1) > 1 && runNumber >= (tm.runCount || 1);
        const ranked = (tm.ranking || []).filter((x) => x.totalTimeMs != null);
        const entries = ranked.map((x) => {
            const runs = {};
            Object.entries(x.runTotals || {}).forEach(([n, t]) => {
                if (t?.totalMs != null) runs[n] = { elapsedMs: t.totalMs };
            });
            if (!Object.keys(runs).length) runs[runNumber] = { elapsedMs: x.totalTimeMs };
            return { ...x, runs, totalMs: x.totalTimeMs, elapsedMs: multi ? x.totalTimeMs : runs[runNumber]?.elapsedMs ?? x.totalTimeMs };
        });
        return multi
            ? { ...tm, runNumber, ranking: entries, combinedRanking: entries }
            : { ...tm, runNumber, runCount: tm.runCount || 1, ranking: entries, combinedRanking: null };
    };
    const [live, setLive] = useState(null);
    const [fisCalc, setFisCalc] = useState(null);
    const [mailType, setMailType] = useState("official");
    const [transport, setTransport] = useState("email");
    const [to, setTo] = useState("results@fis-ski.com");
    const [xmlPreview, setXmlPreview] = useState("");
    const [pdfFile, setPdfFile] = useState(null);
    // PDF officiel généré et joint automatiquement (sinon : fichier choisi).
    const [autoPdf, setAutoPdf] = useState(true);
    const [sending, setSending] = useState(false);
    const [submission, setSubmission] = useState(null);
    const [polling, setPolling] = useState(false);
    // Données du détail de pénalité (Best 10 / Best 5 started), construites
    // au calcul — 08.10.2026, voir fisPenaltyCalc.js.
    const [penaltyCtx, setPenaltyCtx] = useState(null);

    const readError = async (res) => {
        const data = await res.json().catch(() => ({}));
        return data.message || data.error || `HTTP ${res.status}`;
    };

    const loadLive = async (runNumber = run) => {
        setError("");
        if (!(await saveConfig())) return null;
        const res = await apiFetch(`/api/timing/live/${key}?run=${runNumber}`);
        if (!res) return null;
        if (!res.ok) { setError(await readError(res)); return null; }
        const data = await res.json();
        setLive(data);
        setXmlPreview("");
        return data;
    };

    const changeRun = (n) => {
        setRun(n);
        setLive(null);
        setFisCalc(null); // le calcul porte sur une manche précise
        setXmlPreview("");
    };

    const runFisCalc = async () => {
        let liveData = live || (await loadLive());
        if (!liveData) return;
        let data;
        if (support.telemark) {
            // Télémark : calcul ici, sur le classement télémark (temps de ski
            // + pénalités, manches cumulées) — F = 500, max 200.
            const tm = TM_FACTORS[String(config.discipline).toUpperCase()];
            const r = await apiFetch(tmUrl(run, liveData.runCount));
            if (!r) return;
            if (!r.ok) { setError(await readError(r)); return; }
            const tmLive = await r.json();
            const ranking = (tmLive.ranking || []).filter((x) => x.totalTimeMs != null);
            liveData = { ...tmLive, ranking: ranking.map((x) => ({ ...x, elapsedMs: x.totalTimeMs })), combinedRanking: null };
            data = {
                fValue: tm.f,
                maxValue: tm.max,
                racePoints: racePointsFromTimes(ranking, tm.f, (x) => x.totalTimeMs),
                detail: { sumA: 0, sumB: 0, sumC: 0 },
                calculatedPenalty: 0, // remplacé par le détail (A+B−C)/10 ci-dessous
                telemark: true,
            };
        } else {
            const res = await apiFetch(`/api/timing/fis-points-calc/${key}`, {
                method: "POST",
                body: JSON.stringify({ discipline: config.discipline, run }),
            });
            if (!res) return;
            if (!res.ok) { setError(await readError(res)); return; }
            data = await res.json();
        }
        setFisCalc(data);

        // Classés / partants avec leurs points FIS (participants), pour le
        // détail officiel du calcul.
        const getJson = async (url) => {
            const r = await apiFetch(url);
            return r && r.ok ? r.json() : null;
        };
        const athletes = (await getJson(`/api/timing/athletes/${key}`)) || [];
        const byBib = new Map(athletes.filter((a) => a.bib !== "" && a.bib != null).map((a) => [String(a.bib), a]));
        const ident = (r) => {
            const a = byBib.get(String(r.bib)) || {};
            return {
                bib: r.bib,
                rank: r.rank,
                status: r.status,
                fiscode: r.fiscode || a.fiscode || "",
                name: r.name || `${a.lastname || ""} ${a.firstname || ""}`.trim(),
                nation: r.nation || a.nation || "",
                fisPoints: a.points ?? r.points ?? null,
            };
        };
        const multi = (liveData.runCount || 1) > 1 && liveData.combinedRanking?.length && run === (liveData.currentRun || run);
        const classified = (multi ? liveData.combinedRanking : liveData.ranking || []).map(ident);
        const run1 = run > 1 ? await getJson(`/api/timing/live/${key}?run=1`) : liveData;
        const dns1 = new Set((run1?.notClassified || []).filter((n) => n.status === "DNS").map((n) => String(n.bib)));
        const startedMap = new Map();
        [...classified, ...(run1?.ranking || []).map(ident), ...(liveData.notClassified || []).map(ident), ...(run1?.notClassified || []).map(ident)]
            .filter((r) => r.status !== "DNS" && !dns1.has(String(r.bib)))
            .forEach((r) => {
                if (!startedMap.has(String(r.bib))) startedMap.set(String(r.bib), r);
            });
        setPenaltyCtx({ classified, starters: [...startedMap.values()] });

        setConfig((p) => ({
            ...p,
            fisCalculatedPenalty: data.calculatedPenalty,
            fvalue: p.fvalue || data.fValue,
        }));
    };

    // Détail officiel (A, B, C, arrondi, adder, Z, minimum).
    const detail = useMemo(() => {
        if (!fisCalc || !penaltyCtx) return null;
        const racePointsByBib = Object.fromEntries(fisCalc.racePoints.map((r) => [String(r.bib), r.points]));
        return computePenaltyDetail({
            ...penaltyCtx,
            racePointsByBib,
            discipline: config.discipline,
            maxValue: fisCalc.maxValue ?? null,
            categoryAdder: config.fisCategoryAdder,
            correctionZ: config.fisCorrectionZ,
            minimumPenalty: config.fisMinPenalty,
        });
    }, [fisCalc, penaltyCtx, config.discipline, config.fisCategoryAdder, config.fisCorrectionZ, config.fisMinPenalty]);

    // Pénalité appliquée : celle du calcul, sauf saisie manuelle explicite.
    useEffect(() => {
        if (!detail) return;
        // Pénalité calculée (XML <Calculatedpenalty>) = détail officiel.
        setConfig((p) => ({
            ...p,
            fisCalculatedPenalty: detail.calculated.toFixed(2),
            ...(p.fisAppliedManual ? {} : { fisAppliedPenalty: detail.applied.toFixed(2) }),
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail, config.fisAppliedManual]);

    const appliedPenalty = config.fisAppliedManual
        ? parseFloat(config.fisAppliedPenalty) || 0
        : detail?.applied ?? (parseFloat(config.fisAppliedPenalty) || fisCalc?.calculatedPenalty || 0);

    const pointsByBib = useMemo(() => {
        if (!fisCalc) return null;
        const out = {};
        fisCalc.racePoints.forEach((r) => { out[String(r.bib)] = Math.max(0, r.points + appliedPenalty); });
        return out;
    }, [fisCalc, appliedPenalty]);

    const needsPoints = mailType === "official" || mailType === "unofficial";

    const buildBody = async () => {
        let liveData = live || (await loadLive());
        if (!liveData) return null;
        if (config.sector === "TM") {
            const tl = await telemarkLive(run, liveData.runCount);
            if (tl) liveData = tl;
        }
        const slRes = await apiFetch(`/api/timing/startlist/${key}?run=${run}`);
        const startlist = slRes && slRes.ok ? await slRes.json() : [];
        // Code FIS, année, genre, club et nation complétés depuis les
        // participants (les non classés du chrono n'ont que le nom).
        const athRes = await apiFetch(`/api/timing/athletes/${key}`);
        const athletes = athRes && athRes.ok ? await athRes.json() : [];
        // Manches précédentes (manche 2+) : non classés de la manche 1
        // (DNF1…) et temps de manche 1 des non classés de la manche 2.
        const previousLives = [];
        for (let n = 1; n < run; n++) {
            const r = await apiFetch(`/api/timing/live/${key}?run=${n}`);
            if (r && r.ok) previousLives.push({ ...enrichLive(await r.json(), athletes), runNumber: n });
        }
        return {
            config, live: enrichLive(liveData, athletes), mailType, startlist, run,
            racePointsByBib: pointsByBib || {}, previousLives,
        };
    };

    const preview = async () => {
        setError("");
        const body = await buildBody();
        if (!body) return;
        const res = await apiFetch(`/api/timing/fis-email/${key}/xml`, { method: "POST", body: JSON.stringify(body) });
        if (!res) return;
        if (!res.ok) { setError(await readError(res)); return; }
        setXmlPreview(await res.text());
    };

    // Nom FIS des PDF : <saison><secteur><codex><RL|SL>, ex. 2025TM8548RL.pdf
    // (RL = liste de résultats, avec pénalités en télémark ; SL = liste de
    // départ). Objet du mail : FIS/<même nom> — 04.11.2026.
    const fisPdfBase = (kind) => {
        const [, month, year] = String(config.date || "").split(".").map((v) => v?.trim());
        const season = config.season || (year ? Number(year) + (Number(month) >= 7 ? 1 : 0) : "");
        return `${season}${String(config.sector || "AL").toUpperCase()}${config.codex || ""}${kind}`;
    };
    const buildFisPdf = async () => {
        await ensureOwnLogo();
        const getJson = async (u) => {
            const r = await apiFetch(u);
            return r && r.ok ? r.json() : null;
        };
        const athletes = (await getJson(`/api/timing/athletes/${key}`)) || [];
        const isTM = String(config.sector || "").toUpperCase() === "TM";
        let file;
        let kind;
        if (mailType === "startlist") {
            kind = "SL";
            const sl = (await getJson(`/api/timing/startlist/${key}?run=${run}`)) || [];
            file = startlistPdf({ config, eventKey, run, startlist: sl, groupByCategory: false, includeSheet: true, save: false });
        } else {
            kind = "RL";
            const live = enrichLive(await getJson(`/api/timing/live/${key}`), athletes);
            const runCount = live?.runCount || 1;
            if (run < runCount) {
                // Résultats partiels (manche avant la dernière).
                const src = isTM
                    ? telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=${run}&view=run`))
                    : await getJson(`/api/timing/live/${key}?run=${run}`);
                file = runResultsPdf({ config, eventKey, run, live: enrichLive(src, athletes), groupByCategory: false, includeSheet: true, save: false });
            } else {
                let eventLive = live;
                let run1NotClassified = null;
                if (runCount > 1) {
                    const run1 = enrichLive(await getJson(`/api/timing/live/${key}?run=1`), athletes);
                    run1NotClassified = run1?.notClassified || [];
                    eventLive = toEventLive(live, [run1]);
                }
                if (isTM) {
                    const tm = runCount > 1
                        ? await telemarkEventLive(getJson, key, runCount, live.combineMethod)
                        : telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=1&view=run`));
                    if (tm) eventLive = enrichLive({ ...tm, runCount, notClassified: live.notClassified }, athletes);
                }
                file = eventResultsPdf({
                    config, eventKey, live: eventLive, run1NotClassified, pointsByBib: support.supported ? pointsByBib : null,
                    infoLines: fisCalc ? [`FIS list no. ${config.usedFisList || "—"}   Applied penalty: ${fmtPts(appliedPenalty)}   Calculated penalty: ${fmtPts(detail?.calculated ?? fisCalc.calculatedPenalty)}   F-value: ${fisCalc.fValue}`] : [],
                    groupByCategory: false, includeSheet: true, save: false,
                });
            }
        }
        const base = fisPdfBase(kind);
        const dataUrl = await fileToDataUrl(file);
        return { dataUrl, filename: `${base}.pdf`, base };
    };

    const send = async () => {
        setError("");
        setSubmission(null);
        const label = MAIL_TYPES.find((m) => m.value === mailType)?.label;
        const via = transport === "email" ? `email à ${to}` : "l'API FIS Member";
        if (!window.confirm(`Envoyer « ${label} » (manche ${run}) par ${via} ?\n\nC'est un envoi officiel à la FIS : vérifiez l'aperçu XML avant.`)) return;

        setSending(true);
        try {
            const body = await buildBody();
            if (!body) return;
            if (autoPdf && !pdfFile) {
                const gen = await buildFisPdf();
                body.pdfDataUrl = gen.dataUrl;
                body.pdfFilename = gen.filename;
                body.subject = `FIS/${gen.base}`;
            } else {
                body.pdfDataUrl = pdfFile?.dataUrl || null;
                body.pdfFilename = pdfFile?.filename || null;
            }

            if (transport === "email") {
                const res = await apiFetch(`/api/timing/fis-email/${key}/send`, { method: "POST", body: JSON.stringify({ ...body, to }) });
                if (!res) return;
                if (!res.ok) { setError(await readError(res)); return; }
                setStatus(`Envoyé par email à ${to}`);
            } else {
                const res = await apiFetch(`/api/timing/fis-api/${key}/upload`, { method: "POST", body: JSON.stringify(body) });
                if (!res) return;
                if (!res.ok) { setError(await readError(res)); return; }
                const data = await res.json();
                setSubmission(data);
                setStatus(`Envoyé à la FIS (${data.xmlFilename || ""}) : ${data.outcome || "en cours"}`);
            }
        } finally {
            setSending(false);
        }
    };

    // Limite FIS : 1 requête / 5 s / utilisateur sur le statut.
    const poll = async () => {
        if (!submission?.uuid) return;
        setPolling(true);
        try {
            const res = await apiFetch(`/api/timing/fis-api/status/${encodeURIComponent(submission.uuid)}`);
            if (!res) return;
            if (!res.ok) { setError(await readError(res)); return; }
            setSubmission(await res.json());
        } finally {
            setTimeout(() => setPolling(false), 6000);
        }
    };

    const printPenalty = () => {
        if (!fisCalc || !detail) {
            setError("Calculez d'abord les points FIS pour imprimer le détail de la pénalité.");
            return;
        }
        const f2 = (v) => (v == null ? "" : fmtPts(v));
        penaltyPdf({
            config, eventKey, title: "PENALTY CALCULATION",
            infoLines: [
                `F-factor: ${fisCalc.fValue}    Maximum value: ${detail.max}    FIS list n° ${config.usedFisList || "—"}`,
            ],
            tables: [
                {
                    label: "Best 10 at Finish",
                    head: ["Rank", "Bib", "FIS Code", "Name", "NSA", "FIS Points", "Best 5", "Race Points"],
                    body: detail.best10.map((r) => [
                        r.rank ?? "", r.bib, r.fiscode, r.name, r.nation,
                        r.missingPoints ? `(${f2(detail.max)})` : f2(r.fisPointsUsed),
                        r.inBest5 ? f2(r.fisPointsUsed) : "",
                        r.inBest5 ? f2(r.racePoints) : "",
                    ]),
                },
                {
                    label: "Best 5 FIS Points Started",
                    head: ["Rank", "Bib", "FIS Code", "Name", "NSA", "FIS Points"],
                    body: detail.best5Started.map((r) => [r.status || r.rank || "", r.bib, r.fiscode, r.name, r.nation, f2(r.fisPointsUsed)]),
                },
                {
                    label: "Totals",
                    head: ["TOTALS", ""],
                    body: [
                        ["FIS Points of Best 5 Started (B)", f2(detail.sumB)],
                        ["FIS Points of Best 5 at Finish in Top 10 (A)", f2(detail.sumA)],
                        ["Race Points of Corresponding Competitors (C)", f2(detail.sumC)],
                        ["Calculated Penalty (A+B-C) : 10", detail.raw.toFixed(3)],
                        ["Rounded", f2(detail.rounded)],
                        ["Category Adder", f2(detail.categoryAdder)],
                        ["Correction Value (Z)", f2(detail.correctionZ)],
                        ["Calculated Penalty", f2(detail.calculated)],
                        ...(detail.minimumPenalty != null ? [["Minimum Penalty (category)", f2(detail.minimumPenalty)]] : []),
                        ["Penalty Applied", f2(appliedPenalty)],
                    ],
                },
            ],
        });
    };

    const blocking = !isValid || (needsPoints && support.supported && !fisCalc);

    return (
        <>
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>Points FIS</Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Manche</InputLabel>
                        <Select label="Manche" value={run} onChange={(e) => changeRun(Number(e.target.value))}>
                            {[1, 2, 3].map((n) => <MenuItem key={n} value={n}>Manche {n}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={() => loadLive()}>Charger les résultats</Button>
                    {live && <Typography variant="body2" color="text.secondary">{live.ranking?.length || 0} classés</Typography>}
                </Box>

                {!support.supported ? (
                    <Alert severity="info">{support.reason}</Alert>
                ) : (
                    <>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Liste FIS utilisée (n°)" size="small" fullWidth
                                           value={config.usedFisList || ""} onChange={(e) => set("usedFisList", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField label="Valeur F" size="small" fullWidth helperText="Remplie par le calcul"
                                           value={config.fvalue || ""} onChange={(e) => set("fvalue", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Button variant="contained" fullWidth disabled={!config.discipline} onClick={runFisCalc}>
                                    Calculer les points FIS
                                </Button>
                            </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary">
                            Pour les résultats de l'épreuve, calculez sur la dernière manche (classement combiné).
                        </Typography>

                        {fisCalc && detail && (
                            <Box sx={{ mt: 2, p: 2, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Penalty calculation</Typography>
                                    <Typography variant="body2">F-factor : <b>{fisCalc.fValue}</b> — Max : {detail.max}</Typography>
                                </Box>

                                <Typography variant="subtitle2" sx={{ mt: 1 }}>Best 10 at Finish</Typography>
                                <Table size="small" sx={{ "& td, & th": { py: 0.3 } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Rank</TableCell><TableCell>Bib</TableCell><TableCell>FIS Code</TableCell><TableCell>Name</TableCell>
                                            <TableCell>NSA</TableCell><TableCell align="right">FIS Points</TableCell>
                                            <TableCell align="right">Best 5</TableCell><TableCell align="right">Race Points</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.best10.map((r) => (
                                            <TableRow key={r.bib} sx={r.inBest5 ? { "& td": { fontWeight: "bold" } } : undefined}>
                                                <TableCell>{r.rank}</TableCell><TableCell>{r.bib}</TableCell><TableCell>{r.fiscode}</TableCell>
                                                <TableCell>{r.name}</TableCell><TableCell>{r.nation}</TableCell>
                                                <TableCell align="right">{r.missingPoints ? `(${detail.max.toFixed(2)})` : r.fisPointsUsed.toFixed(2)}</TableCell>
                                                <TableCell align="right">{r.inBest5 ? r.fisPointsUsed.toFixed(2) : ""}</TableCell>
                                                <TableCell align="right">{r.inBest5 && r.racePoints != null ? r.racePoints.toFixed(2) : ""}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Typography variant="subtitle2" sx={{ mt: 2 }}>Best 5 FIS Points Started</Typography>
                                <Table size="small" sx={{ "& td, & th": { py: 0.3 } }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Rank</TableCell><TableCell>Bib</TableCell><TableCell>FIS Code</TableCell><TableCell>Name</TableCell>
                                            <TableCell>NSA</TableCell><TableCell align="right">FIS Points</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.best5Started.map((r) => (
                                            <TableRow key={r.bib}>
                                                <TableCell>{r.status || r.rank || ""}</TableCell><TableCell>{r.bib}</TableCell><TableCell>{r.fiscode}</TableCell>
                                                <TableCell>{r.name}</TableCell><TableCell>{r.nation}</TableCell>
                                                <TableCell align="right">{r.fisPointsUsed.toFixed(2)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>

                                <Box sx={{ mt: 2, maxWidth: 560, ml: "auto" }}>
                                    {[
                                        ["FIS Points of Best 5 Started (B)", detail.sumB.toFixed(2)],
                                        ["FIS Points of Best 5 at Finish in Top 10 (A)", detail.sumA.toFixed(2)],
                                        ["Race Points of Corresponding Competitors (C)", detail.sumC.toFixed(2)],
                                        ["Calculated Penalty (A+B−C) : 10", detail.raw.toFixed(3)],
                                        ["Rounded", detail.rounded.toFixed(2)],
                                    ].map(([l, v]) => (
                                        <Box key={l} sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
                                            <Typography variant="body2">{l}</Typography>
                                            <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>{v}</Typography>
                                        </Box>
                                    ))}
                                    <Box sx={{ display: "flex", gap: 1, my: 1, justifyContent: "flex-end", flexWrap: "wrap" }}>
                                        <TextField label="Category Adder" size="small" sx={{ width: 150 }}
                                                   value={config.fisCategoryAdder ?? ""} onChange={(e) => set("fisCategoryAdder", e.target.value)} />
                                        <TextField label="Correction Value (Z)" size="small" sx={{ width: 170 }}
                                                   value={config.fisCorrectionZ ?? ""} onChange={(e) => set("fisCorrectionZ", e.target.value)} />
                                        <TextField label="Minimum (catégorie)" size="small" sx={{ width: 170 }}
                                                   helperText="1re page de la liste FIS"
                                                   value={config.fisMinPenalty ?? ""} onChange={(e) => set("fisMinPenalty", e.target.value)} />
                                    </Box>
                                    <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.25 }}>
                                        <Typography variant="body2">Calculated Penalty</Typography>
                                        <Typography variant="body2" sx={{ fontFamily: "monospace", fontWeight: "bold" }}>{detail.calculated.toFixed(2)}</Typography>
                                    </Box>
                                    {detail.doubleMaxRule && (
                                        <Alert severity="warning" sx={{ my: 1 }}>
                                            Moins de 3 coureurs avec points FIS parmi les 5 retenus, ou au moins 3 classés sans points :
                                            pénalité minimale = double du maximum ({(2 * detail.max).toFixed(2)}), art. 4.4.4.
                                        </Alert>
                                    )}
                                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}>
                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>Penalty Applied</Typography>
                                        <TextField size="small" sx={{ width: 120 }}
                                                   value={config.fisAppliedManual ? config.fisAppliedPenalty ?? "" : appliedPenalty.toFixed(2)}
                                                   onChange={(e) => setConfig((p) => ({ ...p, fisAppliedPenalty: e.target.value, fisAppliedManual: true }))} />
                                    </Box>
                                    {config.fisAppliedManual && (
                                        <Button size="small" onClick={() => setConfig((p) => ({ ...p, fisAppliedManual: false }))}>
                                            Reprendre la valeur calculée ({detail.applied.toFixed(2)})
                                        </Button>
                                    )}
                                    {!fisCalc.telemark && Math.abs((fisCalc.calculatedPenalty ?? detail.rounded) - detail.rounded) > 0.011 && (
                                        <Alert severity="info" sx={{ mt: 1 }}>
                                            Le calcul serveur donne {Number(fisCalc.calculatedPenalty).toFixed(2)} (vs {detail.rounded.toFixed(2)} ici) —
                                            vérifier les points FIS des participants (page Participants, discipline {config.discipline}).
                                        </Alert>
                                    )}
                                </Box>

                                <Typography variant="subtitle2" sx={{ mt: 2 }}>Points de course de tous les classés</Typography>
                                <Table size="small" sx={{ "& td, & th": { py: 0.3 } }}>
                                    <TableHead>
                                        <TableRow><TableCell>Dos.</TableCell><TableCell>Nom</TableCell><TableCell align="right">Points de course</TableCell><TableCell align="right">Points finaux</TableCell></TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {fisCalc.racePoints.map((r) => (
                                            <TableRow key={r.bib}>
                                                <TableCell>{r.bib}</TableCell>
                                                <TableCell>{r.name}</TableCell>
                                                <TableCell align="right">{r.points.toFixed(2)}</TableCell>
                                                <TableCell align="right">{Math.max(0, r.points + appliedPenalty).toFixed(2)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        )}
                    </>
                )}
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" mb={2}>Envoi à la FIS</Typography>
                {!isValid && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Raceheader incomplet : {Object.entries(errors).map(([f, m]) => `${f} (${m})`).join(", ")}
                    </Alert>
                )}

                <Grid container spacing={2} alignItems="flex-start">
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Contenu</InputLabel>
                            <Select label="Contenu" value={mailType} onChange={(e) => { setMailType(e.target.value); setXmlPreview(""); }}>
                                {MAIL_TYPES.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Transport</InputLabel>
                            <Select label="Transport" value={transport} onChange={(e) => { setTransport(e.target.value); setSubmission(null); }}>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="api">API FIS Member</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        {transport === "email" ? (
                            <TextField label="Destinataire" size="small" fullWidth value={to} onChange={(e) => setTo(e.target.value)} />
                        ) : (
                            <Typography variant="caption" color="text.secondary">
                                Nécessite FIS_API_TOKEN côté serveur (scopes competition.files.write et .read).
                                Codex 9991 en mode test.
                            </Typography>
                        )}
                    </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={<Checkbox checked={autoPdf} onChange={(e) => setAutoPdf(e.target.checked)} />}
                        label={`Joindre le PDF officiel généré : ${fisPdfBase(mailType === "startlist" ? "SL" : "RL")}.pdf — objet « FIS/${fisPdfBase(mailType === "startlist" ? "SL" : "RL")} »`}
                    />
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Ou PDF choisi à la main (remplace le PDF généré)</Typography>
                    <input type="file" accept="application/pdf" onChange={async (e) => {
                        const f = e.target.files[0];
                        setPdfFile(f ? { dataUrl: await fileToDataUrl(f), filename: f.name } : null);
                    }} />
                </Box>

                {needsPoints && support.supported && !fisCalc && (
                    <Alert severity="info" sx={{ mt: 2 }}>Calculez les points FIS avant d'envoyer des résultats.</Alert>
                )}

                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button variant="outlined" onClick={preview}>Aperçu XML</Button>
                    <Button variant="contained" color="success" disabled={sending || blocking} onClick={send}>
                        {sending ? "Envoi…" : "Envoyer à la FIS"}
                    </Button>
                </Box>

                {xmlPreview && (
                    <Box component="pre" sx={{
                        mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1, maxHeight: 300, overflow: "auto",
                        fontSize: "0.75rem", whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                        {xmlPreview}
                    </Box>
                )}

                {submission && (
                    <Box sx={{ mt: 2 }}>
                        <Divider sx={{ mb: 2 }} />
                        <Typography variant="body2">
                            Soumission {submission.uuid} : <b>{submission.outcome}</b>
                            {submission.summary?.isComplete ? " (traitement terminé)" : " (en cours)"}
                        </Typography>
                        <Table size="small" sx={{ mt: 1 }}>
                            <TableHead><TableRow><TableCell>Fichier</TableCell><TableCell>Statut</TableCell><TableCell>Commentaire</TableCell></TableRow></TableHead>
                            <TableBody>
                                {(submission.files || []).map((f, i) => (
                                    <TableRow key={i}><TableCell>{f.fileName}</TableCell><TableCell>{f.status}</TableCell><TableCell>{f.comments || ""}</TableCell></TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        {!submission.summary?.isComplete && (
                            <Button size="small" variant="outlined" sx={{ mt: 1 }} disabled={polling} onClick={poll}>
                                {polling ? "Patientez…" : "Vérifier le statut"}
                            </Button>
                        )}
                    </Box>
                )}
            </Paper>

            <PrintPanel
                config={config} eventKey={eventKey} apiFetch={apiFetch} setError={setError}
                pointsByBib={support.supported ? pointsByBib : null}
                pointsMissing={support.supported && !fisCalc ? "Calculez les points FIS (dernière manche) pour imprimer les résultats de l'épreuve." : ""}
                infoLines={fisCalc ? [
                    `FIS list no. ${config.usedFisList || "—"}   Applied penalty: ${fmtPts(appliedPenalty)}   Calculated penalty: ${fmtPts(detail?.calculated ?? fisCalc.calculatedPenalty)}   F-value: ${fisCalc.fValue}`,
                ] : []}
                onPenaltyPdf={support.supported ? printPenalty : undefined}
            />
        </>
    );
}